import { useState } from 'react';
import { Link } from 'react-router-dom';

interface Invoice {
  id: string;
  number: string;
  client: string;
  amount: string;
  status: 'paid' | 'pending' | 'overdue' | 'draft';
  issueDate: string;
  dueDate: string;
}

const MOCK_INVOICES: Invoice[] = [
  { id: '1', number: 'INV-1042', client: 'Horizon Films', amount: '$3,200', status: 'paid', issueDate: '2026-02-15', dueDate: '2026-03-15' },
  { id: '2', number: 'INV-1041', client: 'Bloom Media', amount: '$2,800', status: 'pending', issueDate: '2026-02-10', dueDate: '2026-03-10' },
  { id: '3', number: 'INV-1040', client: 'Vertex Productions', amount: '$5,600', status: 'overdue', issueDate: '2026-01-15', dueDate: '2026-02-15' },
  { id: '4', number: 'INV-1039', client: 'Crestline Studios', amount: '$1,900', status: 'draft', issueDate: '2026-02-25', dueDate: '2026-03-25' },
  { id: '5', number: 'INV-1038', client: 'Horizon Films', amount: '$4,100', status: 'paid', issueDate: '2026-01-05', dueDate: '2026-02-05' },
];

function statusBadgeStyle(status: Invoice['status']): { background: string; color: string } {
  switch (status) {
    case 'paid': return { background: 'rgba(34,197,94,0.15)', color: 'var(--color-success)' };
    case 'pending': return { background: 'rgba(245,158,11,0.15)', color: 'var(--color-warning)' };
    case 'overdue': return { background: 'rgba(239,68,68,0.15)', color: 'var(--color-danger)' };
    case 'draft': return { background: 'rgba(139,143,163,0.15)', color: 'var(--color-text-muted)' };
  }
}

export function InvoicesPage() {
  const [statusFilter, setStatusFilter] = useState<'all' | Invoice['status']>('all');

  const filteredInvoices = MOCK_INVOICES.filter(
    (inv) => statusFilter === 'all' || inv.status === statusFilter,
  );

  const totalOutstanding = MOCK_INVOICES
    .filter((inv) => inv.status === 'pending' || inv.status === 'overdue')
    .length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>Invoices</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            {totalOutstanding} outstanding invoice{totalOutstanding !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/invoices/new"
          style={{
            padding: '10px 20px',
            background: 'var(--color-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 500,
            fontSize: '0.875rem',
            textDecoration: 'none',
          }}
        >
          + New Invoice
        </Link>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {(['all', 'draft', 'pending', 'paid', 'overdue'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: statusFilter === status ? 'var(--color-primary)' : 'transparent',
              color: statusFilter === status ? '#fff' : 'var(--color-text-muted)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'capitalize',
              cursor: 'pointer',
            }}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Invoices table */}
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              {['Invoice', 'Client', 'Amount', 'Status', 'Issue Date', 'Due Date'].map((header) => (
                <th
                  key={header}
                  style={{
                    padding: '14px 16px',
                    textAlign: 'left',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((inv) => {
              const badge = statusBadgeStyle(inv.status);
              return (
                <tr key={inv.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '14px 16px', fontWeight: 500, fontSize: '0.875rem' }}>{inv.number}</td>
                  <td style={{ padding: '14px 16px', fontSize: '0.875rem' }}>{inv.client}</td>
                  <td style={{ padding: '14px 16px', fontSize: '0.875rem', fontWeight: 500 }}>{inv.amount}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textTransform: 'capitalize',
                        ...badge,
                      }}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{inv.issueDate}</td>
                  <td style={{ padding: '14px 16px', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{inv.dueDate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredInvoices.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            No invoices match the selected filter.
          </div>
        )}
      </div>
    </div>
  );
}
